/**
 * HTML auxiliar para impressão de roteiro (RouteModal + TodayRouteModal).
 */

export function escapeRoutePrintHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function routeDefeitoVal(ordem, key, escHtml = escapeRoutePrintHtml) {
  let v = ordem[key] ?? ordem[key.toUpperCase()];
  if (key === 'defeito_descricao' && (v == null || String(v).trim() === '')) {
    v = ordem.TB01048_NOME ?? ordem.defeito ?? null;
  }
  const raw = v != null && String(v).trim() !== '' ? String(v).trim() : '';
  return raw ? escHtml(raw) : '—';
}

export function routeDefeitoObsVal(ordem, escHtml = escapeRoutePrintHtml) {
  const v =
    ordem.defeito_obs ??
    ordem.DEFEITO_OBS ??
    ordem.TB02116_OBS ??
    ordem.defeitoObservacao ??
    ordem.observacao ??
    '';
  const raw = String(v ?? '').trim();
  if (!raw || raw === 'Não informado' || raw === 'N/A') return '';
  return escHtml(raw);
}

export function routeObsGeralVal(ordem, escHtml = escapeRoutePrintHtml) {
  const v =
    ordem.TB02115_OBS ??
    ordem.tb02115_obs ??
    ordem.observacaoGeral ??
    ordem.observacao_geral ??
    '';
  const raw = String(v ?? '').trim();
  return raw ? escHtml(raw) : '';
}

export function routeSolicitanteVal(ordem, escHtml = escapeRoutePrintHtml) {
  const v =
    ordem.solicitante ??
    ordem.SOLICITANTE ??
    ordem.TB02115_SOLICITANTE ??
    ordem.SOLICITANTE_GO ??
    '';
  const raw = String(v ?? '').trim();
  if (!raw || raw === 'N/A' || raw === 'Não informado') return '—';
  return escHtml(raw);
}

/** Preenche obs. defeito e obs. geral via /defeito quando ausentes na listagem (view=card). */
export async function enrichRouteOrdersDefeitoObs(orders, apiBaseUrl, authToken) {
  if (!Array.isArray(orders) || orders.length === 0) return orders;
  return Promise.all(
    orders.map(async (ordem) => {
      const hasDefeitoObs = Boolean(routeDefeitoObsVal(ordem));
      const hasObsGeral = Boolean(routeObsGeralVal(ordem));
      if (hasDefeitoObs && hasObsGeral) return ordem;
      const orderId = ordem.id ?? ordem.TB02115_CODIGO;
      if (!orderId || !apiBaseUrl) return ordem;
      try {
        const res = await fetch(`${apiBaseUrl}/api/orders/${orderId}/defeito`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (!res.ok) return ordem;
        const json = await res.json();
        if (!json?.success || !json?.data) return ordem;
        let next = ordem;
        const obs = json.data.observacao;
        if (!hasDefeitoObs && obs && obs !== 'Não informado') {
          next = { ...next, defeito_obs: obs, TB02116_OBS: obs };
        }
        const obsGeral = String(json.data.observacaoGeral ?? '').trim();
        if (!hasObsGeral && obsGeral) {
          next = { ...next, TB02115_OBS: obsGeral, observacaoGeral: obsGeral };
        }
        return next;
      } catch {
        /* impressão segue sem obs */
      }
      return ordem;
    })
  );
}

export function buildRouteDefeitoPrintRows(ordem, escHtml = escapeRoutePrintHtml) {
  const alerta = routeDefeitoVal(ordem, 'defeito_alert', escHtml);
  const solicitante = routeSolicitanteVal(ordem, escHtml);
  const defeito = routeDefeitoVal(ordem, 'defeito_descricao', escHtml);
  const obsDefeitoRaw = routeDefeitoObsVal(ordem, escHtml);
  const obsDefeito = obsDefeitoRaw || '—';
  const obsGeral = routeObsGeralVal(ordem, escHtml);

  const hasDefeitoLine = [alerta, solicitante, defeito, obsDefeito].some((x) => x !== '—');
  if (!hasDefeitoLine && !obsGeral) return '';

  const defeitoLine = hasDefeitoLine
    ? `<div class="report-defeito-line">
      <span><span class="lbl">Alerta:</span> ${alerta}</span>
      <span class="report-defeito-sep">|</span>
      <span><span class="lbl">Solicitante:</span> ${solicitante}</span>
      <span class="report-defeito-sep">|</span>
      <span><span class="lbl">Defeito:</span> ${defeito}</span>
      <span class="report-defeito-sep">|</span>
      <span><span class="lbl">Obs. defeito:</span> ${obsDefeito}</span>
    </div>`
    : '';

  const obsLine = obsGeral
    ? `<div class="report-defeito-line report-defeito-line--obs"><span class="lbl">Observações gerais:</span> ${obsGeral}</div>`
    : '';

  return `
    <tr class="report-defeito-row">
      <td colspan="10">
        <div class="report-defeito-block">
          ${defeitoLine}
          ${obsLine}
        </div>
      </td>
    </tr>`;
}

export const ROUTE_PRINT_DEFEITO_CSS = `
  .report-defeito-row td {
    background: #f8fafc;
    padding: 6px 8px 8px;
    border-bottom: 1px solid #e2e8f0;
    white-space: normal;
    vertical-align: top;
  }
  .report-defeito-block {
    font-size: 8px;
    line-height: 1.45;
    color: #475569;
  }
  .report-defeito-block .lbl {
    font-weight: 600;
    color: #1e293b;
    margin-right: 4px;
  }
  .report-defeito-line {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
  }
  .report-defeito-line--obs {
    margin-top: 4px;
    display: block;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .report-defeito-sep {
    color: #cbd5e1;
    user-select: none;
  }
`;
